import {
  LinearProgress,
  Typography,
  withStyles
} from '@material-ui/core'
import { Theme } from '@material-ui/core/styles'
import ChartTimelineVariantIcon from 'mdi-react/ChartTimelineVariantIcon'
import RocketLaunchIcon from 'mdi-react/RocketLaunchIcon'
import { FC, Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import Container from '../../../components/Container'
import { selectApartmentById } from '../../../selectors/apartments'
import { selectContentIdFromPathname } from '../../../selectors/router'
import Dialog from '../../Dialog/Dialog'
import PaymentPopup from '../../PayForPublish/PaymentPopup'
import { MuiClasses } from '../../../types'
import { Apartment } from '../../../types/apartments'
import { StateInterface } from '../../../types/states'


const styles = (theme: Theme) => ({
  wrapper: {
    backgroundColor: '#F7F8FA',
    padding: theme.spacing(5, 4, 6),
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(4, 3, 5)
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(3, 2.5, 4)
    }
  },
  titleRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3)
  },
  pageTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(0.5)
  },
  pageSubtitle: {
    color: '#4B5563'
  },
  generatedAt: {
    color: '#6B7280',
    fontSize: 14
  },
  summaryRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, 1fr)',
    gap: theme.spacing(3),
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr'
    }
  },
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 20,
    padding: theme.spacing(4),
    boxShadow: '0 10px 30px rgba(20, 16, 47, 0.08)',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: theme.spacing(3)
  },
  summaryHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  summaryTitle: {
    fontSize: 20,
    fontWeight: 700,
    marginBottom: theme.spacing(0.5)
  },
  summaryDescription: {
    color: '#6B7280'
  },
  tableTitle: {
    marginLeft: theme.spacing(2)
  },
  summaryIcon: {
    width: 56,
    height: 56,
    borderRadius: '16px',
    background: '#E2EBFD',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#94004A'
  },
  metricsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: theme.spacing(3)
  },
  metricLabel: {
    textTransform: 'uppercase' as const,
    fontSize: 12,
    fontWeight: 600,
    color: '#6B7280',
    letterSpacing: 0.4
  },
  metricValueRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1)
  },
  metricValue: {
    fontSize: 34,
    fontWeight: 700,
    color: '#111827'
  },
  metricPeriod: {
    fontSize: 13,
    color: '#6B7280'
  },
  marketingMeta: {
    color: '#6B7280',
    marginTop: theme.spacing(1)
  },
  ctaBody: {
    color: '#4B5563'
  },
  ctaButtons: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: theme.spacing(1.5)
  },
  ctaPrimaryButton: {
    backgroundColor: '#94004A',
    color: '#FFFFFF',
    padding: theme.spacing(1.5, 3),
    borderRadius: 8,
    '&:hover': {
      backgroundColor: '#7A003B'
    }
  },
  tableSection: {
    backgroundColor: '#FFFFFF',
    borderRadius: 20,
    padding: theme.spacing(3),
    boxShadow: '0 10px 30px rgba(20, 16, 47, 0.05)'
  },
  tableWrapper: {
    overflowX: 'auto' as const
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    minWidth: 720
  },
  tableHeadCell: {
    textTransform: 'uppercase' as const,
    fontSize: 12,
    letterSpacing: 0.4,
    color: '#6B7280',
    fontFamily: theme.typography.fontFamily,
    textAlign: 'left' as const,
    padding: theme.spacing(1.5, 2),
    borderBottom: '2px solid #E5E7EB'
  },
  tableCell: {
    padding: theme.spacing(1.5, 2),
    borderBottom: '1px solid #F3F4F6',
    fontSize: 14,
    color: '#111827',
    fontFamily: theme.typography.fontFamily
  },
  tableRowLabel: {
    fontWeight: 600
  },
  tableSummaryRow: {
    backgroundColor: '#E2EBFD'
  },
  tableSummaryLabel: {
    fontWeight: 600
  },
  tableSummaryCell: {
    fontWeight: 600
  }
})

type SummaryMetric = {
  current?: number
  total?: number
  periodLabel?: string
}

type MarketingInfo = {
  days?: number
  publishedAt?: string
}

type DailyRow = {
  dayLabel: string
  visits?: number
  visitors?: number
  alerts?: number
  favorites?: number
  contacts?: number
  timestamp?: number
}

type DailySummary = Omit<DailyRow, 'dayLabel' | 'timestamp'>

interface ApartmentStatisticsProps {
  apartment?: Apartment
  classes: MuiClasses
}

const ApartmentStatistics: FC<ApartmentStatisticsProps> = ({
  apartment,
  classes
}) => {
  const { t } = useTranslation()

  if (!apartment) {
    return <LinearProgress />
  }

  const statistics = apartment.oviProStatistics
  if (!statistics) {
    return <LinearProgress />
  }

  const dailyRows = extractDailyRows(statistics)
  const summary = extractSummary(statistics)
  const tableSummary = buildDailySummary(dailyRows.slice(-7))
  const updatedAt = getUpdatedAtLabel(statistics)

  return (
    <Fragment>
      <Container alignCenter={false} className={classes.wrapper}>
        <div className={classes.titleRow}>
          <div>
            <Typography variant='h4' className={classes.pageTitle}>
              {t('statisticsPage.title')}
            </Typography>
            <Typography variant='body1' className={classes.pageSubtitle}>
              {t('statisticsPage.subtitle')}
            </Typography>
          </div>
          {updatedAt && (
            <Typography variant='body2' className={classes.generatedAt}>
              {t('statisticsPage.updatedAt', { date: updatedAt })}
            </Typography>
          )}
        </div>

        <div className={classes.summaryRow}>
          <div className={classes.card}>
            <div className={classes.summaryHeader}>
              <div>
                <Typography className={classes.summaryTitle}>
                  {t('statisticsPage.summary.title')}
                </Typography>
                <Typography className={classes.summaryDescription}>
                  {t('statisticsPage.summary.subtitle')}
                </Typography>
              </div>
              <div className={classes.summaryIcon}>
                <ChartTimelineVariantIcon size={28} />
              </div>
            </div>
            <div className={classes.metricsGrid}>
              <SummaryMetricCard
                label={t('statisticsPage.visitsLabel')}
                metric={summary.visits}
                classes={classes}
              />
              <SummaryMetricCard
                label={t('statisticsPage.table.columns.contactsAll')}
                metric={summary.contacts}
                classes={classes}
              />
              <MarketingCard
                label={t('statisticsPage.marketingLabel')}
                marketing={summary.marketing}
                classes={classes}
                daysLabel={t('statisticsPage.daysLabel')}
                publishedText={
                  summary.marketing?.publishedAt
                    ? `${t('statisticsPage.marketingPublished')} ${formatFullDate(
                        summary.marketing.publishedAt
                      )}`.trim()
                    : undefined
                }
              />
            </div>
          </div>
          <div className={classes.card}>
            <div className={classes.summaryHeader}>
              <Typography className={classes.summaryTitle}>
                {t('apartment.list.information.listingVisibility')}
              </Typography>
              <div className={classes.summaryIcon}>
                <RocketLaunchIcon size={28} />
              </div>
            </div>
            <Typography className={classes.ctaBody}>
              {t('apartment.listingVisibilityHelper')}
            </Typography>
            <div className={classes.ctaButtons}>
              <Dialog
                dialogId='StatisticsRefreshOnPlatforms'
                color='primary'
                size='medium'
                maxWidth='md'
                buttonLabel={t('apartment.refreshOnPlatforms')}
                buttonClassName={classes.ctaPrimaryButton}
                Content={
                  <PaymentPopup
                    productTags={['refreshOnPlatforms']}
                    headertext={t('paymentPopup.headerTexts.refreshOnPlatforms')}
                    infotext={t('paymentPopup.suggestTexts.refreshOnPlatforms')}
                    apartment={apartment}
                  />
                }
              />
            </div>
          </div>
        </div>

        {dailyRows.length > 0 && (
          <div className={classes.tableSection}>
            <Typography
              variant='h6'
              className={`${classes.summaryTitle} ${classes.tableTitle}`}
            >
              {t('statisticsPage.table.title')}
            </Typography>
            <div className={classes.tableWrapper}>
              <table className={classes.table}>
                <thead>
                  <tr>
                    <th className={classes.tableHeadCell}>
                      {t('statisticsPage.table.columns.day')}
                    </th>
                    <th className={classes.tableHeadCell}>
                      {t('statisticsPage.table.columns.pageVisits')}
                    </th>
                    <th className={classes.tableHeadCell}>
                      {t('statisticsPage.table.columns.visitors')}
                    </th>
                    <th className={classes.tableHeadCell}>
                      {t('statisticsPage.table.columns.alerts')}
                    </th>
                    <th className={classes.tableHeadCell}>
                      {t('statisticsPage.table.columns.favorites')}
                    </th>
                    <th className={classes.tableHeadCell}>
                      {t('statisticsPage.table.columns.contactsAll')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {dailyRows.map((row) => (
                    <tr key={row.dayLabel}>
                      <td
                        className={`${classes.tableCell} ${classes.tableRowLabel}`}
                      >
                        {row.dayLabel}
                      </td>
                      <td className={classes.tableCell}>
                        {formatNumericValue(row.visits)}
                      </td>
                      <td className={classes.tableCell}>
                        {formatNumericValue(row.visitors)}
                      </td>
                      <td className={classes.tableCell}>
                        {formatNumericValue(row.alerts)}
                      </td>
                      <td className={classes.tableCell}>
                        {formatNumericValue(row.favorites)}
                      </td>
                      <td className={classes.tableCell}>
                        {formatNumericValue(row.contacts)}
                      </td>
                    </tr>
                  ))}
                  {tableSummary && (
                    <tr className={classes.tableSummaryRow}>
                      <td
                        className={`${classes.tableCell} ${classes.tableRowLabel} ${classes.tableSummaryLabel}`}
                      >
                        {t('statisticsPage.table.summaryLabel')}
                      </td>
                      <td className={`${classes.tableCell} ${classes.tableSummaryCell}`}>
                        {formatNumericValue(tableSummary.visits)}
                      </td>
                      <td className={`${classes.tableCell} ${classes.tableSummaryCell}`}>
                        {formatNumericValue(tableSummary.visitors)}
                      </td>
                      <td className={`${classes.tableCell} ${classes.tableSummaryCell}`}>
                        {formatNumericValue(tableSummary.alerts)}
                      </td>
                      <td className={`${classes.tableCell} ${classes.tableSummaryCell}`}>
                        {formatNumericValue(tableSummary.favorites)}
                      </td>
                      <td className={`${classes.tableCell} ${classes.tableSummaryCell}`}>
                        {formatNumericValue(tableSummary.contacts)}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Container>
    </Fragment>
  )
}

type SummaryMetricCardProps = {
  label: string
  metric?: SummaryMetric
  classes: MuiClasses
}

const SummaryMetricCard = ({
  label,
  metric,
  classes
}: SummaryMetricCardProps) => {
  const primaryValue = formatNumericValue(metric?.current ?? metric?.total)
  const periodLabel = metric?.periodLabel

  return (
    <div>
      <Typography className={classes.metricLabel}>{label}</Typography>
      <div className={classes.metricValueRow}>
        <Typography className={classes.metricValue}>{primaryValue}</Typography>
      </div>
      {periodLabel && (
        <Typography className={classes.metricPeriod}>{periodLabel}</Typography>
      )}
    </div>
  )
}

type MarketingCardProps = {
  label: string
  marketing?: MarketingInfo
  classes: MuiClasses
  publishedText?: string
  daysLabel?: string
}

const MarketingCard = ({
  label,
  marketing,
  classes,
  publishedText,
  daysLabel
}: MarketingCardProps) => (
  <div>
    <Typography className={classes.metricLabel}>{label}</Typography>
    <div className={classes.metricValueRow}>
      <Typography className={classes.metricValue}>
        {formatNumericValue(marketing?.days)}
        {marketing?.days !== undefined && daysLabel ? ` ${daysLabel}` : ''}
      </Typography>
    </div>
    {publishedText && (
      <Typography className={classes.marketingMeta}>{publishedText}</Typography>
    )}
  </div>
)

const numberFrom = (...values: unknown[]): number | undefined => {
  for (const value of values) {
    const parsed = parseNumber(value)
    if (parsed !== undefined) {
      return parsed
    }
  }
  return undefined
}

const parseNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value
  }

  if (typeof value === 'string') {
    const normalized = value.replace(',', '.').trim()
    if (normalized === '') {
      return undefined
    }
    const parsed = Number(normalized)
    return Number.isNaN(parsed) ? undefined : parsed
  }

  return undefined
}

const stringFrom = (...values: unknown[]): string | undefined => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim().length > 0) {
      return value.trim()
    }
  }

  return undefined
}

const flattenValue = (value: any) => {
  if (!value || typeof value !== 'object') {
    return value
  }

  return {
    ...value,
    ...(value.value && typeof value.value === 'object' ? value.value : {}),
    ...(value.current && typeof value.current === 'object' ? value.current : {}),
    ...(value.data && typeof value.data === 'object' ? value.data : {}),
    ...(value.metrics && typeof value.metrics === 'object' ? value.metrics : {})
  }
}

const extractSummary = (
  statistics: any
): { visits?: SummaryMetric; contacts?: SummaryMetric; marketing?: MarketingInfo } => {
  const visitValue = numberFrom(statistics?.visitCount)
  const contactValue = numberFrom(statistics?.contactCount)
  const marketing = buildMarketingInfo(statistics?.daysInMarketingStatus)

  const visitsMetric = visitValue !== undefined ? { total: visitValue } : undefined
  const contactsMetric = contactValue !== undefined ? { total: contactValue } : undefined

  return {
    visits: visitsMetric,
    contacts: contactsMetric,
    marketing
  }
}

const extractDailyRows = (statistics: any): DailyRow[] => {
  if (!statistics) {
    return []
  }

  const arrays = [
    statistics?.daily,
    statistics?.dailyStats,
    statistics?.dailyStatistics,
    statistics?.rows,
    statistics?.history,
    statistics?.timeline,
    statistics?.table?.rows,
    statistics?.table,
    statistics?.entries,
    statistics?.data,
    statistics?.days
  ].filter((value) => Array.isArray(value)) as Array<any[]>

  if (arrays.length === 0) {
    return []
  }

  const normalized: DailyRow[] = []
  const seenLabels = new Set<string>()

  arrays
    .reduce((acc, curr) => acc.concat(curr), [] as any[])
    .forEach((row) => {
      const normalizedRow = normalizeDailyRow(row)
      if (normalizedRow && !seenLabels.has(normalizedRow.dayLabel)) {
        seenLabels.add(normalizedRow.dayLabel)
        normalized.push(normalizedRow)
      }
    })

  return normalized.sort((a, b) => {
    const timeA = a.timestamp ?? 0
    const timeB = b.timestamp ?? 0
    return timeA - timeB
  })
}

const normalizeDailyRow = (row: any): DailyRow | undefined => {
  if (!row || typeof row !== 'object') {
    return undefined
  }

  const flattened = flattenValue(row)
  const dateCandidate = flattened?.timePeriod
  const dateValue = parseDateValue(dateCandidate)
  const label =
    stringFrom(flattened?.label, flattened?.dayLabel, flattened?.name, flattened?.title) ||
    (dateValue ? formatShortDate(dateValue) : undefined)

  if (!label) {
    return undefined
  }

  const visits = numberFrom(flattened?.visitCount)
  const visitors = numberFrom(flattened?.uniqueVisitorCount)
  const alerts = numberFrom(flattened?.watchdogEmailCount)
  const favorites = numberFrom(flattened?.userFavoriteCount)
  const contacts = numberFrom(flattened?.contactCount)

  if (
    visits === undefined &&
    visitors === undefined &&
    alerts === undefined &&
    favorites === undefined &&
    contacts === undefined
  ) {
    return undefined
  }

  return {
    dayLabel: label,
    visits,
    visitors,
    alerts,
    favorites,
    contacts,
    timestamp: dateValue?.valueOf()
  }
}

const buildDailySummary = (rows: DailyRow[]): DailySummary | undefined => {
  if (!rows.length) {
    return undefined
  }

  const summary: DailySummary = {}
  const keys: Array<keyof DailySummary> = [
    'visits',
    'visitors',
    'alerts',
    'favorites',
    'contacts'
  ]
  let hasValue = false

  keys.forEach((key) => {
    rows.forEach((row) => {
      const value = row[key]
      if (typeof value === 'number') {
        summary[key] = (summary[key] || 0) + value
        hasValue = true
      }
    })
  })

  return hasValue ? summary : undefined
}

const fiNumberFormatter = new Intl.NumberFormat('fi-FI')
const fiShortDateFormatter = new Intl.DateTimeFormat('fi-FI', {
  day: 'numeric',
  month: 'short',
  year: 'numeric'
})
const fiFullDateFormatter = new Intl.DateTimeFormat('fi-FI')

const formatNumericValue = (value?: number): string => {
  if (value === undefined || value === null) {
    return '-'
  }
  return fiNumberFormatter.format(value)
}

const parseDateValue = (value: any): Date | undefined => {
  if (!value) {
    return undefined
  }

  if (value instanceof Date) {
    return Number.isNaN(value.valueOf()) ? undefined : value
  }

  if (typeof value === 'number') {
    const date = new Date(value)
    return Number.isNaN(date.valueOf()) ? undefined : date
  }

  if (typeof value === 'string') {
    const numeric = Number(value)
    if (!Number.isNaN(numeric) && value.trim() !== '') {
      const date = new Date(numeric)
      if (!Number.isNaN(date.valueOf())) {
        return date
      }
    }
    const parsed = new Date(value)
    return Number.isNaN(parsed.valueOf()) ? undefined : parsed
  }

  return undefined
}

const formatShortDate = (value: any): string | undefined => {
  const date = parseDateValue(value)
  if (!date) {
    return typeof value === 'string' ? value : undefined
  }
  return fiShortDateFormatter.format(date)
}

const formatFullDate = (value: any): string => {
  const date = parseDateValue(value)
  if (!date) {
    return typeof value === 'string' ? value : ''
  }
  return fiFullDateFormatter.format(date)
}

function buildMarketingInfo(daysInMarketingStatus: unknown): MarketingInfo | undefined {
  const daysFromMarketingStatus = numberFrom(daysInMarketingStatus)
  const days = daysFromMarketingStatus
  if (days === undefined) {
    return undefined
  }

  return {
    days
  }
}

const getUpdatedAtLabel = (statistics: any): string | undefined => {
  const value = stringFrom(
    statistics?.generatedAt,
    statistics?.updatedAt,
    statistics?.createdAt
  )
  return value ? formatFullDate(value) : undefined
}

const mapStateToProps = (state: StateInterface) => {
  const apartmentId = selectContentIdFromPathname(state)

  return {
    apartment: apartmentId ? selectApartmentById(apartmentId)(state) : undefined
  }
}

export default connect(
  mapStateToProps
)(withStyles(styles)(ApartmentStatistics as any))
